import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Music2,
  AudioWaveform,
  FileAudio,
  MessageSquare,
  Zap,
  ArrowRight,
  Play,
  Headphones,
  Guitar,
  Drum,
  Mic2,
  Piano,
  Sliders,
  Youtube,
  Download,
  SlidersHorizontal,
  Video
} from "lucide-react";

const LandingPage = () => {
  const features = [
    {
      icon: <Play className="w-6 h-6" />,
      title: "Player con Acordes Sincronizados",
      description: "Reproduce cualquier canción viendo los acordes en tiempo real. El acorde actual siempre visible."
    },
    {
      icon: <AudioWaveform className="w-6 h-6" />,
      title: "Separación de Stems",
      description: "Aísla voces, batería, bajo e instrumentos con IA. Practica con la pista que necesitas."
    },
    {
      icon: <Music2 className="w-6 h-6" />,
      title: "Análisis Musical Completo",
      description: "Detecta tono, BPM, acordes y estructura automáticamente. Todo sincronizado con el tiempo."
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Asistente Armónico IA",
      description: "Sugerencias inteligentes de acordes, puentes, variaciones y rearmonizaciones."
    }
  ];

  const roles = [
    { icon: <Mic2 className="w-5 h-5" />, name: "Cantantes", desc: "Tonalidad, transposición, playback sin voz" },
    { icon: <Drum className="w-5 h-5" />, name: "Bateristas", desc: "BPM preciso, estructura, stem de batería" },
    { icon: <Guitar className="w-5 h-5" />, name: "Bajistas", desc: "Root notes, groove, línea de bajo" },
    { icon: <Piano className="w-5 h-5" />, name: "Tecladistas", desc: "Acordes sincronizados, inversiones" },
    { icon: <Headphones className="w-5 h-5" />, name: "Productores", desc: "Stems, exportar todo, preproducción" },
    { icon: <Sliders className="w-5 h-5" />, name: "Percusionistas", desc: "Ritmo, clave, patrón, groove" }
  ];

  const stats = [
    { value: "< 30s", label: "Análisis completo" },
    { value: "99%", label: "Precisión BPM" },
    { value: "4", label: "Stems separados" },
    { value: "∞", label: "Posibilidades" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-signal to-tune flex items-center justify-center">
              <AudioWaveform className="w-5 h-5 text-ink" />
            </div>
            <span className="font-display font-bold text-xl text-foreground">SteelMora</span>
          </Link>

          <div className="hidden xl:flex items-center gap-5">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Características</a>
            <a href="#roles" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Para ti</a>
            <a href="#free-tools" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Herramientas gratis</a>
            <Link to="/youtube-practice" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Practicar</Link>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Planes</Link>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link to="/auth" className="hidden sm:block">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground" data-testid="login-btn">
                Iniciar sesión
              </Button>
            </Link>
            <Link to="/auth?mode=register">
              <Button className="min-h-11 px-3 text-xs sm:text-sm bg-primary hover:bg-primary/90 btn-glow" data-testid="signup-btn">
                Empezar gratis
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm mb-8 animate-fade-in">
              <Zap className="w-4 h-4" />
              <span>Una herramienta para cualquier músico</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6 leading-tight animate-slide-up" data-testid="hero-title">
              Entiende la música
              <span className="text-gradient"> como nunca antes</span>
            </h1>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: "0.1s" }}>
              Analiza canciones, separa instrumentos y reproduce con acordes sincronizados.
              El mismo motor, una vista para cada músico.
            </p>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: "0.2s" }}>
              <Link to="/auth?mode=register">
                <Button size="lg" className="bg-primary hover:bg-primary/90 btn-glow text-lg px-8 h-14 gap-2" data-testid="cta-btn">
                  Comenzar ahora
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/youtube-practice">
                <Button size="lg" variant="outline" className="border-signal/40 bg-signal/10 text-signal hover:bg-signal/20 text-lg px-8 h-14 gap-2">
                  <Youtube className="w-5 h-5" />
                  Practicar con YouTube
                </Button>
              </Link>
              <Link to="/download-songs">
                <Button size="lg" variant="outline" className="border-tune/40 bg-tune/10 text-tune hover:bg-tune/20 text-lg px-8 h-14 gap-2" data-testid="download-music-btn">
                  <Download className="w-5 h-5" />
                  Descargar Música
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-panel text-lg px-8 h-14 gap-2">
                <Play className="w-5 h-5" />
                Ver demo
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-slide-up" style={{ animationDelay: `${0.3 + index * 0.1}s` }}>
                <div className="text-3xl md:text-4xl font-bold text-foreground font-mono tabular-nums">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="free-tools" aria-labelledby="free-tools-title" className="scroll-mt-20 border-y border-border px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <h2 id="free-tools-title" className="font-display text-2xl font-bold sm:text-3xl">Herramientas gratuitas</h2>
            <span className="text-sm text-tune">Gratis · Sin registro</span>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[{ to: "/pitch-tempo", title: "Cambiar tono y BPM", Icon: SlidersHorizontal,
                copy: "Cambia la tonalidad y velocidad de cualquier audio de forma independiente.", color: "text-brass" },
              { to: "/video-audio", title: "Extraer audio de video", Icon: Video,
                copy: "Extrae el audio de un video o descarga el video sin su pista de audio.", color: "text-signal" },
              { to: "/creator-studio", title: "CREATOR STUDIO", Icon: Video,
                copy: "Combina video, audio, imágenes, títulos y grabaciones para crear contenido profesional.", color: "text-brass" }].map(({ to, title, Icon, copy, color }) => (
              <article key={to} className="flex min-w-0 flex-col items-start rounded-md border border-border bg-panel p-5 sm:p-6">
                <Icon className={`mb-4 h-7 w-7 ${color}`} />
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mb-5 mt-2 text-sm text-muted-foreground">{copy}</p>
                <Link to={to} className={`mt-auto inline-flex min-h-11 items-center gap-2 text-sm font-semibold ${color}`} data-testid={to === "/pitch-tempo" ? "public-pitch-entry" : to === "/creator-studio" ? "public-creator-entry" : "public-video-entry"}>
                  {to === "/creator-studio" ? "Crear proyecto" : "Usar gratis"} <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Preview - Player */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden border border-border bg-panel p-2">
            <div className="absolute top-4 left-4 flex gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>

            <div className="pt-8 p-6 space-y-6">
              {/* Current Chord - Big Display */}
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Acorde Actual</p>
                <div className="inline-block px-8 py-4 rounded-xl border-2 bg-primary/20 border-primary/40">
                  <span className="text-5xl font-bold text-primary font-mono">Em</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Siguiente: <span className="text-foreground/80 font-mono">C</span>
                </p>
              </div>

              {/* Progress Bar */}
              <div className="relative h-3 bg-panel-raised rounded-full">
                <div className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-primary to-secondary rounded-full" />
                {/* Section markers */}
                <div className="absolute top-0 h-full border-l border-border" style={{ left: "25%" }} />
                <div className="absolute top-0 h-full border-l border-border" style={{ left: "50%" }} />
                <div className="absolute top-0 h-full border-l border-border" style={{ left: "75%" }} />
              </div>

              {/* Chord Timeline */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {["G", "Em", "C", "D", "G", "Em", "Am", "D7"].map((chord, i) => (
                  <div
                    key={i}
                    className={`flex-shrink-0 px-4 py-3 rounded-lg border transition-all ${
                      i === 1
                        ? "bg-primary/20 border-primary/40 text-primary scale-110"
                        : "bg-ink/40 border-border text-muted-foreground"
                    }`}
                  >
                    <span className="font-mono font-semibold">{chord}</span>
                  </div>
                ))}
              </div>

              {/* Analysis Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-ink/40 rounded-lg p-4 border border-border">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Tonalidad</div>
                  <div className="text-2xl font-bold text-foreground font-mono mt-1">G Major</div>
                </div>
                <div className="bg-ink/40 rounded-lg p-4 border border-border">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">BPM</div>
                  <div className="text-2xl font-bold text-secondary font-mono tabular-nums mt-1">128</div>
                </div>
                <div className="bg-ink/40 rounded-lg p-4 border border-border">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Sección</div>
                  <div className="text-2xl font-bold text-accent font-mono mt-1">Verso</div>
                </div>
                <div className="bg-ink/40 rounded-lg p-4 border border-border">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">Duración</div>
                  <div className="text-2xl font-bold text-foreground font-mono tabular-nums mt-1">3:42</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Todo lo que necesitas
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Un motor de análisis. Múltiples formas de verlo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-panel border border-border rounded-xl p-8 card-hover"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="py-20 px-6 bg-ink-raised">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Hecho para ti
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              No importa qué instrumento toques. SteelMora se adapta.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {roles.map((role, index) => (
              <div
                key={index}
                className="bg-panel border border-border rounded-xl p-6 hover:border-primary/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  {role.icon}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{role.name}</h3>
                <p className="text-sm text-muted-foreground">{role.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Así de simple
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Sube tu audio", desc: "Arrastra un archivo MP3, WAV o cualquier formato de audio" },
              { step: "02", title: "Análisis instantáneo", desc: "Tono, BPM, acordes y estructura en segundos" },
              { step: "03", title: "Reproduce y aprende", desc: "Player sincronizado con acordes, stems separados" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-foreground/5 font-mono absolute -top-4 left-0">
                  {item.step}
                </div>
                <div className="pt-12 pl-4">
                  <h3 className="text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl p-6 sm:p-12 border border-primary/20">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Empieza a entender la música
            </h2>
            <p className="text-xl text-foreground/80 mb-8">
              Únete a músicos que ya usan SteelMora para mejorar su práctica
            </p>
            <Link to="/auth?mode=register">
              <Button size="lg" className="max-w-full bg-ivory text-ink hover:bg-ivory/90 text-base sm:text-lg px-4 sm:px-10 h-14 gap-2 font-semibold">
                Crear cuenta gratis
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground mt-4">Sin tarjeta de crédito requerida</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-signal to-tune flex items-center justify-center">
              <AudioWaveform className="w-5 h-5 text-ink" />
            </div>
            <span className="font-display font-bold text-lg text-foreground">SteelMora</span>
          </div>
          <div className="text-sm text-muted-foreground">
            © 2025 SteelMora. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
